
import SpendUsageBarChart, {
    convertProjectionsResponseToDataset,
    convertUsageResponseToDatasets,
    IClickEvent,
    UsageReportTypes,
} from '@/components/Domain.Usage/SpendUsageBarChart.vue';
import {
    SpendUsageOptions,
    convertSpendOptionsToQuery,
} from '@/components/Domain.Usage/SpendUsageOptionsToolbarForm.vue';
import { filterUsageParamsByFilterChips, IChipViewModel } from '@/components/Domain.Usage/UsageFilterChips.vue';
import { toastError } from '@/components/Common/Toast.vue';
import BudgetsTable from '@/components/Domain.Budgets/BudgetsTable.vue';
import {
    PluralEntityType,
    ICustomViewResult,
    Granularity,
    IBudgetResult,
    ISpendUsageTableRecordsViewModel,
    SpendFieldNames,
} from '@/models';
import EntityType, { fromPlural } from '@/models/EntityType';
import { useTenantStore, useWaitStore, storeToRefs } from '@/stores';
import { mapStores } from 'pinia';
import { getSpend, getForecast } from '@/lib/Api';
import { defineComponent, ref, watch } from 'vue';
import { i18n } from '@/i18n';
import * as Api from '@/lib/Api';
import { SpendUsageTableFunctions } from '@/components/Domain.Usage/SpendUsageTableFunctions';

export default defineComponent({
    data() {
        return { isShowingReport: false, customViews: [], isCreating: false, customViewsLoading: true };
    },
    components: { SpendUsageBarChart, BudgetsTable },
    props: { value: Object, loading: Boolean },
    computed: {
        ...mapStores(useTenantStore),
        routePrefix() {
            return `/customViews/${this.value.id}`;
        },
    },
    setup(p) {
        const budgets = ref<IBudgetResult[]>([]);
        const loadBudgets = async () =>
            await whilst('gettingBudgets', async function () {
                const view = p.value as ICustomViewResult;
                budgets.value = await Api.getCustomViewBudgets(view.id);
            });

        const { currencyCode } = storeToRefs(useTenantStore());
        const { is, whilst } = useWaitStore();
        const spendOptions = ref<SpendUsageOptions>({
            dateRangeKey: '13m',
            forecastRangeKey: '3m',
            granularity: Granularity.monthly,
            topXResults: 10,
        });
        const newChartData = ref({ labels: [], datasets: [] });
        const usageTableItems = ref<ISpendUsageTableRecordsViewModel | null>(null);
        const filters = ref<IChipViewModel[]>([]);
        const averageMonthlySpend = { value: 0.0 };
        const maxDate = ref<string | null>(null);
        const loadTabUsage = async () =>
            await whilst('getAccountUsage', async () => {
                const view = p.value as ICustomViewResult;
                let params = convertSpendOptionsToQuery(spendOptions.value);
                const segregateBy = spendOptions.value.segregateBy as PluralEntityType;
                params = filterUsageParamsByFilterChips(filters.value, params);
                if (!params) return;
                const [usageResponse, projectionsResponse] = await Promise.all([
                    getSpend({
                        ...params,
                        savedViewId: view.id,
                        segregateBy: spendOptions.value.segregateBy,
                        fields: [SpendFieldNames.Charges],
                    }),
                    getForecast(
                        { ...params, savedViewId: view.id },
                        spendOptions.value.dateRangeKey,
                        spendOptions.value.forecastRangeKey
                    ),
                ]);
                const usageDataset = convertUsageResponseToDatasets(usageResponse, {
                    reportType: UsageReportTypes.charges,
                });
                const forecastDataset = convertProjectionsResponseToDataset(projectionsResponse);
                newChartData.value = {
                    labels: [...forecastDataset.labels, ...usageDataset.labels],
                    datasets: [...forecastDataset.datasets, ...usageDataset.datasets],
                };
                usageTableItems.value = SpendUsageTableFunctions.convertToItemRecords(
                    params,
                    segregateBy,
                    usageResponse,
                    spendOptions.value.costView
                );
                averageMonthlySpend.value = usageResponse.recordAverage || 0.0;
                maxDate.value = projectionsResponse?.maxUsageDate || params.toDate;
            });

        const handleDatasetClicked = ({ isOther, name, datasetGroupId }: IClickEvent) => {
            if (isOther) {
                spendOptions.value.topXResults = spendOptions.value.topXResults + 10;
            } else if (spendOptions.value.segregateBy === PluralEntityType.meters) {
                toastError(i18n.t('shared.meterInformationNotSupported'));
            } else {
                filters.value.push({
                    id: datasetGroupId,
                    name,
                    type: fromPlural(spendOptions.value.segregateBy) as EntityType,
                });
            }
        };

        watch(
            () => [p.value, spendOptions.value, filters.value],
            () => {
                if (!p.value || !spendOptions.value || is('getAccountUsage')) return;
                loadTabUsage();
                loadBudgets();
            },
            { immediate: true, deep: true }
        );

        return {
            spendOptions,
            newChartData,
            usageTableItems,
            filters,
            loadTabUsage,
            handleDatasetClicked,
            averageMonthlySpend,
            currencyCode,
            wait: { is },
            budgets,
            loadBudgets,
            maxDate,
        };
    },
});
